/**
 * Companies List Page
 * Listagem de empresas: filtros, seis colunas e as ações da carteira.
 *
 * O topo tem uma única ação primária ("Nova empresa"); os três destinos de
 * aplicação em massa ficam num menu para não disputar com ela.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Menu, MenuItem, Tooltip } from '@mui/material';
import {
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { CompanyFilters, CompanyTable } from './components';
import { PageShell } from '../../components';
import { RouteActionButton } from '../../components/common';
import type { DataTableSortOrder } from '../../components';

import { companyService } from '../../services/companyService';
import type { Company, CompanyFilters as CompanyFiltersType } from '../../types';
import { CompanyStatus, ModuleType } from '../../types';

import { pode } from '../../authorization/pode';
import { useVeredito } from '../../authorization/useVeredito';
import { useCompanyExport } from '../../hooks/useCompanyExport';
import { useManifestoStore } from '../../stores/manifestoStore';

interface CompanyFiltersData {
  searchTerm: string;
  status: string[];
  states: string[];
  companyTypes: string[];
  modules: string[];
}

/**
 * Aplicar em massa alcança as empresas todas de uma vez: é operação de Plataforma. O item
 * continua à vista fora dela, desabilitado, dizendo qual contexto abre a tela — a auditoria
 * achou este botão aberto para os 15 contextos, inclusive laboratório e perfil só de leitura,
 * e quem não enxerga o item nunca descobre que basta trocar de contexto para usá-lo.
 */
const APLICACOES_EM_MASSA = [
  { label: 'Aplicar riscos', path: '/risk-applications', permissao: 'risk_application.read' },
  { label: 'Aplicar fontes', path: '/source-applications', permissao: 'source_application.read' },
  { label: 'Aplicar exames', path: '/exam-applications', permissao: 'exam_application.read' },
] as const;

const toApiFilters = (filtros: CompanyFiltersData): Partial<CompanyFiltersType> => {
  const apiFilters: Partial<CompanyFiltersType> = {};

  if (filtros.searchTerm) apiFilters.search = filtros.searchTerm;
  if (filtros.status.length > 0) apiFilters.status = filtros.status.join(',');
  if (filtros.states.length > 0) apiFilters.state = filtros.states.join(',');
  if (filtros.companyTypes.length > 0) apiFilters.companyType = filtros.companyTypes.join(',');
  if (filtros.modules.length > 0) apiFilters.modules = filtros.modules as ModuleType[];

  return apiFilters;
};

/**
 * A barra de filtros nasce com "Situação: Ativa" — esse estado é a listagem
 * padrão, não um filtro que o usuário aplicou. Só o que sai daí muda o vazio de
 * "nada cadastrado" para "filtro sem resultado".
 */
const temFiltroAplicado = (filtros: CompanyFiltersData): boolean =>
  filtros.searchTerm.trim() !== '' ||
  filtros.states.length > 0 ||
  filtros.companyTypes.length > 0 ||
  filtros.modules.length > 0 ||
  !(filtros.status.length === 1 && filtros.status[0] === CompanyStatus.ACTIVE);

const mensagemDoErro = (erro: unknown, padrao: string): string =>
  erro instanceof Error && erro.message ? erro.message : padrao;

const CompaniesListPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const abrirFicha = useVeredito('/companies/:id', pode('company.read'));
  const aplicarRiscos = useVeredito(
    APLICACOES_EM_MASSA[0].path,
    pode(APLICACOES_EM_MASSA[0].permissao, { perspectiva: 'plataforma' })
  );
  const aplicarFontes = useVeredito(
    APLICACOES_EM_MASSA[1].path,
    pode(APLICACOES_EM_MASSA[1].permissao, { perspectiva: 'plataforma' })
  );
  const aplicarExames = useVeredito(
    APLICACOES_EM_MASSA[2].path,
    pode(APLICACOES_EM_MASSA[2].permissao, { perspectiva: 'plataforma' })
  );
  const perspectiva = useManifestoStore((estado) => estado.manifesto?.perspectiva);
  const { exporting, exportCompanies } = useCompanyExport();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState('displayName');
  const [sortOrder, setSortOrder] = useState<DataTableSortOrder>('asc');
  const [filtrosAplicados, setFiltrosAplicados] = useState<CompanyFiltersData | null>(null);
  const [filtrosKey, setFiltrosKey] = useState(0);
  const [recarregar, setRecarregar] = useState(0);
  const [menuMassa, setMenuMassa] = useState<HTMLElement | null>(null);

  // A barra de filtros aplica sozinha ao montar: carregar antes dela seria a
  // mesma requisição duas vezes a cada abertura da tela.
  useEffect(() => {
    if (!filtrosAplicados) return undefined;

    let ativo = true;

    const carregar = async (): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        const resposta = await companyService.getCompanies({
          page,
          limit: rowsPerPage,
          search: filtrosAplicados.searchTerm,
          filters: toApiFilters(filtrosAplicados),
          sortBy,
          sortOrder,
        });

        if (!ativo) return;
        setCompanies(resposta.data);
        setTotalCount(resposta.pagination.total);
      } catch (erro: unknown) {
        if (!ativo) return;
        setError(mensagemDoErro(erro, 'Erro ao carregar empresas'));
      } finally {
        if (ativo) setLoading(false);
      }
    };

    carregar();

    return () => {
      ativo = false;
    };
  }, [filtrosAplicados, page, rowsPerPage, sortBy, sortOrder, recarregar]);

  const aplicarFiltros = (filtros: CompanyFiltersData): void => {
    setFiltrosAplicados(filtros);
    setPage(1);
  };

  const limparFiltros = (): void => {
    setFiltrosKey((key) => key + 1);
    setPage(1);
  };

  const alterarOrdem = (by: string, order: DataTableSortOrder): void => {
    setSortBy(by);
    setSortOrder(order);
    setPage(1);
  };

  const alternarSituacao = async (company: Company): Promise<void> => {
    try {
      await companyService.toggleCompanyStatus(company.id.toString());
      const acao = company.status === CompanyStatus.ACTIVE ? 'inativada' : 'ativada';
      enqueueSnackbar(`Empresa ${acao} com sucesso`, { variant: 'success' });
      setRecarregar((n) => n + 1);
    } catch (erro: unknown) {
      enqueueSnackbar(mensagemDoErro(erro, 'Erro ao alterar status da empresa'), {
        variant: 'error',
      });
    }
  };

  const exportarSelecao = (selecionadas: Company[]): void => {
    const linhas = [
      ['Empresa', 'Razão social', 'CNPJ', 'Cidade', 'UF', 'Situação'],
      ...selecionadas.map((company) => [
        company.tradeName || company.legalName,
        company.legalName,
        company.cnpj,
        company.city ?? '',
        company.state,
        company.status,
      ]),
    ];

    const csv = linhas.map((linha) => linha.map((celula) => `"${celula}"`).join(';')).join('\n');
    // BOM: sem ele o Excel em pt-BR abre o arquivo com os acentos quebrados.
    const url = window.URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'empresas-selecionadas.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const aplicacoesEmMassa = [
    { ...APLICACOES_EM_MASSA[0], veredito: aplicarRiscos },
    { ...APLICACOES_EM_MASSA[1], veredito: aplicarFontes },
    { ...APLICACOES_EM_MASSA[2], veredito: aplicarExames },
  ];

  // "Enxerga todas as empresas?" é a perspectiva do contexto ativo, não o papel de quem olha:
  // só na Plataforma não há recorte, e só ali o vazio pode afirmar que não há nada cadastrado.
  const semEscopo = perspectiva !== 'plataforma';
  const comFiltro = filtrosAplicados ? temFiltroAplicado(filtrosAplicados) : false;

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Empresas' }]}
      title="Empresas"
      actions={
        <>
          <Button
            variant="outlined"
            endIcon={<ArrowDropDownIcon />}
            onClick={(event) => setMenuMassa(event.currentTarget)}
          >
            Aplicar em massa
          </Button>
          <Menu anchorEl={menuMassa} open={Boolean(menuMassa)} onClose={() => setMenuMassa(null)}>
            {aplicacoesEmMassa.map(({ label, path, veredito }) => (
              <Tooltip key={path} title={veredito.explicacao ?? ''} placement="left">
                <span>
                  <MenuItem
                    disabled={!veredito.liberado}
                    onClick={() => {
                      setMenuMassa(null);
                      navigate(path);
                    }}
                  >
                    {label}
                  </MenuItem>
                </span>
              </Tooltip>
            ))}
          </Menu>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={() => exportCompanies(filtrosAplicados ? toApiFilters(filtrosAplicados) : {})}
            disabled={exporting}
          >
            Exportar
          </Button>
          <RouteActionButton
            para="/companies/create"
            exige={pode('company.create')}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Nova empresa
          </RouteActionButton>
        </>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <CompanyFilters
          key={filtrosKey}
          onFilter={aplicarFiltros}
          loading={exporting}
          total={totalCount}
        />
      </Box>

      <CompanyTable
        companies={companies}
        totalCount={totalCount}
        page={page - 1}
        rowsPerPage={rowsPerPage}
        loading={loading}
        sort={{ by: sortBy, order: sortOrder, onChange: alterarOrdem }}
        noCompaniesInScope={semEscopo}
        filtersActive={comFiltro}
        onClearFilters={limparFiltros}
        onPageChange={(novaPagina) => setPage(novaPagina + 1)}
        onRowsPerPageChange={(novoTamanho) => {
          setRowsPerPage(novoTamanho);
          setPage(1);
        }}
        onExportSelection={exportarSelecao}
        onView={abrirFicha.liberado ? (company) => navigate(`/companies/${company.id}`) : undefined}
        onEdit={(company) => navigate(`/companies/${company.id}/edit`)}
        onToggleStatus={alternarSituacao}
      />
    </PageShell>
  );
};

export default CompaniesListPage;
